import { useEffect } from "react";
import { useLocation } from "wouter";
import { canonicalUrlFor } from "@/lib/seo-constants";

interface SEOProps {
  title: string;
  description?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  twitterCard?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  noIndex?: boolean;
  canonicalPath?: string;
}

export function useSEO({
  title,
  description,
  ogTitle,
  ogDescription,
  ogType = "website",
  twitterCard = "summary_large_image",
  twitterTitle,
  twitterDescription,
  noIndex = false,
  canonicalPath,
}: SEOProps) {
  const [location] = useLocation();
  const path = canonicalPath ?? location;
  const canonicalUrl = canonicalUrlFor(path);

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? "property" : "name";
      let element = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attr, name);
        document.head.appendChild(element);
      }
      element.content = content;
    };

    const setLink = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      if (!element) {
        element = document.createElement("link");
        element.rel = rel;
        document.head.appendChild(element);
      }
      element.href = href;
    };

    if (description) setMeta("description", description);
    if (ogTitle) setMeta("og:title", ogTitle, true);
    if (ogDescription) setMeta("og:description", ogDescription, true);
    if (ogType) setMeta("og:type", ogType, true);
    setMeta("og:url", canonicalUrl, true);
    if (twitterCard) setMeta("twitter:card", twitterCard);
    if (twitterTitle) setMeta("twitter:title", twitterTitle);
    if (twitterDescription) setMeta("twitter:description", twitterDescription);
    
    setLink("canonical", canonicalUrl);
    
    if (noIndex) {
      setMeta("robots", "noindex, nofollow");
    } else {
      setMeta("robots", "index, follow");
    }

    return () => {
      // Cleanup handled by next page setting new values
    };
  }, [title, description, ogTitle, ogDescription, ogType, twitterCard, twitterTitle, twitterDescription, noIndex, canonicalUrl]);
}
